// src/Pages/DistrictBDPOReport.jsx - GRAM SACHIV VERSION
import { useState, useEffect } from "react";
import {
    Button,
    Table,
    Spin,
    Alert,
    message,
    Space
} from "antd";
import {
    FileExcelOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import {
    getPanchayatSpecialGsReport,
    getAdminGpSummaryReport
} from "../api/User";

const DistrictSachivReport = ({ dateFilter }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Read directly from sessionStorage (synchronous — no async state race)
    const roleId = sessionStorage.getItem("role_id");
    const userInfo = {
        district_code: sessionStorage.getItem("district_code"),
        district_name: sessionStorage.getItem("district_name"),
        block_code: sessionStorage.getItem("block_code"),
        block_name: sessionStorage.getItem("block_name"),
        panchayat_code: sessionStorage.getItem("panchayat_code"),
        panchayat_name: sessionStorage.getItem("panchayat_name"),
    };

    const numberSorter = (a, b, key) => {
        const valA = a[key] || 0;
        const valB = b[key] || 0;
        return valA - valB;
    };

    const stringSorter = (a, b, key) => {
        const valA = (a[key] || '').toString().toLowerCase();
        const valB = (b[key] || '').toString().toLowerCase();
        return valA.localeCompare(valB);
    };

    // Check if current user is Gram Sachiv
    const isGramSachiv = roleId === "2";

    /* ================= GRAM SACHIV PANCHAYAT COLUMNS ================= */
    const gramSachivColumns = [
        {
            title: "Sr. No.",
            dataIndex: "sr_no",
            key: "sr_no",
            width: 60,
            align: "center",
            fixed: "left",
            sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
            title: "District Name",
            dataIndex: "district_name",
            key: "district_name",
            width: 140,
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'district_name'),
            ellipsis: true,
        },
        {
            title: "Block Name",
            dataIndex: "block_name",
            key: "block_name",
            width: 160,
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'block_name'),
            ellipsis: true,
        },
        {
            title: "Gram Panchayat Name",
            dataIndex: "gp_name",
            key: "gp_name",
            width: 200,
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'gp_name'),
            ellipsis: true,

        },

        {
            title: "Gram Sabha Held (including 1st,2nd & 3rd)",
            dataIndex: "gram_sabha_held",
            key: "gram_sabha_held",
            width: 130,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'gram_sabha_held'),
            render: (val) => <span >{val || 0}</span>,
        },
        {
            title: "Quorum Fulfilled (including 1st,2nd & 3rd)",
            dataIndex: "quorum_fulfilled",
            key: "quorum_fulfilled",
            width: 130,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'quorum_fulfilled'),
            render: (val) => <span >{val || 0}</span>,
        },
        {
            title: "Quorum Failed (including 1st,2nd & 3rd)",
            dataIndex: "quorum_failed",
            key: "quorum_failed",
            width: 110,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'quorum_failed'),
            render: (val) => <span >{val || 0}</span>,
        },
        {
            title: "Never Scheduled Gram Sabha",
            dataIndex: "never_scheduled_gs",
            key: "never_scheduled_gs",
            width: 120,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'never_scheduled_gs'),
            render: (val) => <span>{val || 0}</span>,
        },
    ];

    /* ================= REGULAR BLOCK COLUMNS ================= */
    const blockColumns = [
        {
            title: "Sr. No.",
            dataIndex: "sr_no",
            key: "sr_no",
            width: 60,
            align: "center",
            fixed: "left",
            sorter: (a, b) => numberSorter(a, b, 'sr_no'),
        },
        {
            title: "District Name",
            dataIndex: "district_name",
            key: "district_name",
            width: 160,
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'district_name'),
            ellipsis: true,
        },
        {
            title: "Block Name",
            dataIndex: "block_name",
            key: "block_name",
            width: 180,
            align: "center",
            sorter: (a, b) => stringSorter(a, b, 'block_name'),
            ellipsis: true,
        },
        {
            title: "No. of Gram Panchayats",
            dataIndex: "gp_count",
            key: "gp_count",
            width: 160,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'gp_count'),
            render: (val, record) => (
                <span style={{ fontWeight: 500 }}>
                    {val || 0}
                </span>
            ),
        },
        {
            title: "Gram Sabha Held (including 1st,2nd & 3rd)",
            dataIndex: "gram_sabha_held",
            key: "gram_sabha_held",
            width: 140,
            align: "center",
            sorter: (a, b) => numberSorter(a, b, 'gram_sabha_held'),
            render: (val) => <span>{val || 0}</span>,
        },
        {
            title: "Quorum Fulfilled (including 1st,2nd & 3rd meeting)",
            dataIndex: "quorum_fulfilled",
            key: "quorum_fulfilled",
            width: 130,
            align: "center",
            render: (val) => <span>{val || 0}</span>,
            sorter: (a, b) => numberSorter(a, b, 'quorum_fulfilled'),
        },
        {
            title: "Quorum Failed (including 1st,2nd & 3rd meeting)",
            dataIndex: "quorum_failed",
            key: "quorum_failed",
            width: 120,
            align: "center",
            render: (val) => <span>{val || 0}</span>,
            sorter: (a, b) => numberSorter(a, b, 'quorum_failed'),
        },
        {
            title: "Never Scheduled Gram Sabha",
            dataIndex: "never_scheduled_gs",
            key: "never_scheduled_gs",
            width: 140,
            align: "center",
            render: (val) => <span>{val || 0}</span>,
            sorter: (a, b) => numberSorter(a, b, 'never_scheduled_gs'),
        },
    ];

    // Get columns based on role
    const getColumns = () => {
        return isGramSachiv ? gramSachivColumns : blockColumns;
    };

    // Fetch Gram Sachiv's own panchayat data
    const fetchGramSachivData = async (currentDateFilter) => {
        try {
            setLoading(true);
            setError(null);

            const token = sessionStorage.getItem("token");

            if (!token) {
                setError("No authentication token found. Please login again.");
                message.error("Authentication required");
                return;
            }

            if (!userInfo.district_code || !userInfo.block_code || !userInfo.panchayat_code) {
                setError("User location information not found. Please contact administrator.");
                message.error("Location information missing");
                return;
            }

            console.log("Fetching Gram Sachiv panchayat data...");
            console.log("User Info:", userInfo);

            // Fetch GP level data for Gram Sachiv's panchayat
            const result = await getAdminGpSummaryReport(
                token,
                userInfo.district_code,
                userInfo.block_code,
                { period: currentDateFilter }
            );

            if (result.success) {
                // Filter only this Gram Sachiv's panchayat
                const panchayatData = result.result.filter(item =>
                    item.panchayat_code === userInfo.panchayat_code
                );

                const formatted = panchayatData.map((item, index) => ({
                    ...item,
                    key: item.panchayat_code || `gp-${index}`,
                    sr_no: index + 1,
                    district_name: item.district_name || userInfo.district_name,
                    block_name: item.block_name || userInfo.block_name,
                    gp_name: item.panchayat_name || userInfo.panchayat_name || "N/A",
                    // Split gram sabha counts by attempt
                    first_gs_held: item.first_gramsabha_held || 0,
                    second_gs_held: item.second_gramsabha_held || 0,
                    third_gs_held: item.third_gramsabha_held || 0,
                    gram_sabha_held: item.total_panchayats_gramsabha_held || 0,
                    quorum_fulfilled: item.quorum_fulfilled_count || 0,
                    quorum_failed: item.quorum_failed_count || 0,
                    never_scheduled_gs: item.never_scheduled_gramsabha || 0,
                }));

                setData(formatted);

                if (formatted.length > 0) {
                    message.success(`Loaded data for ${formatted[0].gp_name}`);
                } else {
                    message.warning("No data found for your Gram Panchayat");
                }
            } else {
                message.error(result.message || 'Failed to fetch panchayat data');
            }
        } catch (err) {
            console.error("Fetch Error Details:", err);
            const errorMsg = err.message || "Failed to fetch report data";
            setError(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Fetch block level data for other roles
    const fetchBlockReportData = async (currentDateFilter) => {
        try {
            setLoading(true);
            setError(null);

            const token = sessionStorage.getItem("token");

            if (!token) {
                setError("No authentication token found. Please login again.");
                message.error("Authentication required");
                return;
            }

            console.log("Fetching BDPO panchayat report with filter:", currentDateFilter);

            const res = await getPanchayatSpecialGsReport(token, { period: currentDateFilter });

            console.log("Full API Response:", res);

            // Handle the response - it returns panchayat-level data with 'status' and 'data'
            if (res.status === 'success' && res.data) {
                const reportData = res.data || [];

                const formatted = reportData.map((item, index) => {
                    return {
                        ...item,
                        key: item.panchayat_code || `panchayat-${index}`,
                        sr_no: index + 1,
                        gp_name: item.panchayat_name || "N/A",
                        // Map the panchayat data to expected format
                        first_gs_held: item.gs_scheduled || 0,
                        second_gs_held: 0, // Not available in this API
                        third_gs_held: 0, // Not available in this API
                        gram_sabha_held: item.gs_conducted || 0,
                        quorum_fulfilled: item.gs_conducted || 0, // Assuming conducted means fulfilled
                        quorum_failed: 0, // Not available in this API
                        never_scheduled_gs: item.total_gp - (item.gs_scheduled || 0),
                        district_code: item.district_code,
                        district_name: item.district_name,
                        block_code: item.block_code,
                        block_name: item.block_name,
                    };
                });

                setData(formatted);

                if (formatted.length > 0) {
                    message.success(`Loaded ${formatted.length} panchayat records`);
                } else {
                    message.warning("No panchayat data found");
                }
            } else {
                message.error(res.message || 'Failed to fetch panchayat data');
            }
        } catch (err) {
            console.error("Fetch Error Details:", err);
            const errorMsg = err.message || "Failed to fetch report data";
            setError(errorMsg);
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isGramSachiv) {
            if (userInfo.panchayat_code) {
                fetchGramSachivData(dateFilter);
            } else {
                fetchBlockReportData(dateFilter);
            }
        } else {
            fetchBlockReportData(dateFilter);
        }
    }, [dateFilter]);

    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    /* ================= EXPORT FUNCTIONS ================= */
    const handleExportExcel = () => {
        if (!data.length) {
            message.warning("No data to export");
            return;
        }

        try {
            let excelData;

            if (isGramSachiv) {
                // Gram Sachiv export format
                excelData = data.map((row, idx) => ({
                    "Sr. No.": idx + 1,
                    "District Name": row.district_name || "-",
                    "Block Name": row.block_name || "-",
                    "Gram Panchayat Name": row.gp_name || "-",
                    "1st Gram Sabha Held": row.first_gs_held || 0,
                    "2nd Gram Sabha Held": row.second_gs_held || 0,
                    "3rd Gram Sabha Held": row.third_gs_held || 0,
                    "Total Gram Sabha Held": row.gram_sabha_held || 0,
                    "Quorum Fulfilled": row.quorum_fulfilled || 0,
                    "Quorum Failed": row.quorum_failed || 0,
                    "Never Scheduled": row.never_scheduled_gs || 0,
                }));

                // Add grand total row for Gram Sachiv
                const totals = {
                    "Sr. No.": "GRAND TOTAL",
                    "District Name": "",
                    "Block Name": "",
                    "Gram Panchayat Name": "",
                    "1st Gram Sabha Held": data.reduce((s, i) => s + (i.first_gs_held || 0), 0),
                    "2nd Gram Sabha Held": data.reduce((s, i) => s + (i.second_gs_held || 0), 0),
                    "3rd Gram Sabha Held": data.reduce((s, i) => s + (i.third_gs_held || 0), 0),
                    "Total Gram Sabha Held": data.reduce((s, i) => s + (i.gram_sabha_held || 0), 0),
                    "Quorum Fulfilled": data.reduce((s, i) => s + (i.quorum_fulfilled || 0), 0),
                    "Quorum Failed": data.reduce((s, i) => s + (i.quorum_failed || 0), 0),
                    "Never Scheduled": data.reduce((s, i) => s + (i.never_scheduled_gs || 0), 0),
                };
                excelData.push(totals);
            } else {
                // Regular export format for other roles
                excelData = data.map((row, idx) => ({
                    "Sr. No.": idx + 1,
                    "District Name": row.district_name || "-",
                    "Block Name": row.block_name || "-",
                    "No. of Gram Panchayats": row.gp_count || 0,
                    "Gram Sabha Held (including 1st,2nd & 3rd)": row.gram_sabha_held || 0,
                    "Quorum Fulfilled (including 1st, 2nd & 3rd)": row.quorum_fulfilled || 0,
                    "Quorum Failed (including 1st, 2nd & 3rd)": row.quorum_failed || 0,
                    "Never Scheduled Gram Sabha": row.never_scheduled_gs || 0,
                }));

                // Add grand total row for block view
                const blockTotals = {
                    "Sr. No.": "GRAND TOTAL",
                    "District Name": "",
                    "Block Name": "",
                    "No. of Gram Panchayats": data.reduce((s, i) => s + (i.gp_count || 0), 0),
                    "Gram Sabha Held (including 1st,2nd & 3rd)": data.reduce((s, i) => s + (i.gram_sabha_held || 0), 0),
                    "Quorum Fulfilled (including 1st, 2nd & 3rd)": data.reduce((s, i) => s + (i.quorum_fulfilled || 0), 0),
                    "Quorum Failed (including 1st, 2nd & 3rd)": data.reduce((s, i) => s + (i.quorum_failed || 0), 0),
                    "Never Scheduled Gram Sabha": data.reduce((s, i) => s + (i.never_scheduled_gs || 0), 0),
                };
                excelData.push(blockTotals);
            }

            const worksheet = XLSX.utils.json_to_sheet(excelData);
            const workbook = XLSX.utils.book_new();

            const sheetName = isGramSachiv ? "Panchayat Report" : "Block Report";
            XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

            const fileName = isGramSachiv
                ? `Gram_Panchayat_Report_${userInfo.panchayat_name?.replace(/\s+/g, '_') || 'MyGP'}_${new Date().toISOString().split('T')[0]}.xlsx`
                : `Block_Special_GS_Report_${new Date().toISOString().split('T')[0]}.xlsx`;

            XLSX.writeFile(workbook, fileName);
            message.success("Excel file exported successfully");
        } catch (err) {
            console.error("Excel Export Error:", err);
            message.error("Failed to export Excel");
        }
    };

    // Calculate grand totals for Gram Sachiv view
    const gramSachivTotals = {
        first_gs_held: data.reduce((s, i) => s + (i.first_gs_held || 0), 0),
        second_gs_held: data.reduce((s, i) => s + (i.second_gs_held || 0), 0),
        third_gs_held: data.reduce((s, i) => s + (i.third_gs_held || 0), 0),
        gram_sabha_held: data.reduce((s, i) => s + (i.gram_sabha_held || 0), 0),
        quorum_fulfilled: data.reduce((s, i) => s + (i.quorum_fulfilled || 0), 0),
        quorum_failed: data.reduce((s, i) => s + (i.quorum_failed || 0), 0),
        never_scheduled_gs: data.reduce((s, i) => s + (i.never_scheduled_gs || 0), 0),
    };

    // Calculate grand totals for block view
    const blockTotals = {
        gp_count: data.reduce((s, i) => s + (i.gp_count || 0), 0),
        gram_sabha_held: data.reduce((s, i) => s + (i.gram_sabha_held || 0), 0),
        quorum_fulfilled: data.reduce((s, i) => s + (i.quorum_fulfilled || 0), 0),
        quorum_failed: data.reduce((s, i) => s + (i.quorum_failed || 0), 0),
        never_scheduled_gs: data.reduce((s, i) => s + (i.never_scheduled_gs || 0), 0),
    };

    // Get report title based on role
    const getReportTitle = () => {
        if (isGramSachiv) {
            return `Gram Panchayat Status Report: ${userInfo.panchayat_name || ''} (Gram Sachiv)`;
        }
        return "Panchayat-wise Status Report (BDPO)";
    };

    return (
        <div className="report-container">
            <div style={{
                background: '#fff',
                padding: '24px',
                borderRadius: '8px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                marginBottom: '24px'
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px'
                }}>
                    <div>
                        <h2 style={{
                            fontSize: '20px',
                            fontWeight: '600',
                            marginBottom: '4px',
                            color: '#1f1f1f'
                        }}>
                            {getReportTitle()}
                        </h2>
                        <p style={{
                            fontSize: '14px',
                            color: '#666',
                            margin: 0
                        }}>
                            {isGramSachiv ? (
                                <>

                                </>
                            ) : (
                                <>Total Records: <strong>{data.length}</strong> panchayats</>
                            )}
                        </p>
                    </div>

                    <Space>
                        <Button
                            type="primary"
                            icon={<FileExcelOutlined />}
                            onClick={handleExportExcel}
                            disabled={loading || data.length === 0}
                        >
                            Export Excel
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => isGramSachiv ? fetchGramSachivData(dateFilter) : fetchBlockReportData(dateFilter)}
                            disabled={loading}
                        >
                            Refresh
                        </Button>
                    </Space>
                </div>

                {error && (
                    <Alert
                        message={error}
                        type="error"
                        showIcon
                        style={{ marginBottom: '16px' }}
                    />
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: '10px', color: '#666' }}>
                            {isGramSachiv ? 'Loading panchayat data...' : 'Loading block report data...'}
                        </p>
                    </div>
                ) : (
                    <Table
                        columns={getColumns()}
                        dataSource={data}
                        rowKey="key"
                        bordered
                        size="small"
                        scroll={{ x: isGramSachiv ? 1500 : 1200, y: 420 }}
                        pagination={{
                            current: currentPage,
                            pageSize: pageSize,
                            total: data.length,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50", "100"],
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} of ${total} records`,
                        }}
                        onChange={handleTableChange}
                    />
                )}

                {data.length > 0 && !isGramSachiv && (
                    <div
                        style={{
                            marginTop: 12,
                            padding: "10px 14px",
                            background: "#e6f7ff",
                            border: "1px solid #91d5ff",
                            borderRadius: 6,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 14,
                            fontSize: 13,
                        }}
                    >
                        <span><strong>Grand Totals (All Panchayats):</strong></span>
                        <span>GPs: <strong>{blockTotals.gp_count}</strong></span>
                        <span>GS Held: <strong>{blockTotals.gram_sabha_held}</strong></span>
                        <span>Quorum Met: <strong>{blockTotals.quorum_fulfilled}</strong></span>
                        <span>Quorum Failed: <strong>{blockTotals.quorum_failed}</strong></span>
                        <span>Never Scheduled: <strong>{blockTotals.never_scheduled_gs}</strong></span>
                    </div>
                )}

                {data.length > 0 && isGramSachiv && (
                    <div
                        style={{
                            marginTop: 12,
                            padding: "10px 14px",
                            background: "#e6f7ff",
                            border: "1px solid #91d5ff",
                            borderRadius: 6,
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 14,
                            fontSize: 13,
                        }}
                    >
                        <span><strong>Gram Panchayat Totals:</strong></span>

                        <span>GS Held: <strong>{gramSachivTotals.gram_sabha_held}</strong></span>
                        <span>Quorum Fullfilled: <strong>{gramSachivTotals.quorum_fulfilled}</strong></span>
                        <span>Quorum Failed: <strong>{gramSachivTotals.quorum_failed}</strong></span>
                        <span>Never Scheduled: <strong>{gramSachivTotals.never_scheduled_gs}</strong></span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DistrictSachivReport;